/* eslint-disable react-hooks/rules-of-hooks */
import React, { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Spinner from '@fenderdigital/ui/Spinner';
import ChordDiagram from '@fenderdigital/notation/components/chord-diagram';
import { up } from 'styled-breakpoints';
import SectionTitle from '../SectionTitle';
import Chords from '../../lib/chords';

type Record = {
  [key: string]: any;
};

interface ChordOverlayProps {
  chords?: Record[] | Record;
  title?: string | null;
}

const ChordDiagramsContainer = styled.div`
  display: block;
  ${up('tablet-portrait')} {
    padding-left: 1rem;
  }
`;

const ChordsContainer = styled.div`
  display: grid;
  grid-gap: 15px;
  justify-content: space-evenly;
  grid-template-columns: repeat(auto-fit, minmax(125px, 45%));

  ${up('phone')} {
    column-gap: 20px;
    grid-template-columns: repeat(auto-fit, minmax(125px, 40%));
  }
  @media screen and (min-width: 640px) {
    justify-content: stretch;
    column-gap: 20px;
    grid-template-columns: repeat(auto-fit, minmax(125px, 22%));
  }
  ${up('tablet-portrait')} {
    column-gap: 25px;
  }
  ${up('tablet-landscape')} {
    column-gap: 5px;
    grid-template-columns: repeat(auto-fit, minmax(125px, 16%));
  }
  ${up('laptop')} {
    grid-template-columns: repeat(auto-fit, minmax(155px, 200px));
  }
`;

function renderChordDiagrams(chords: Record[]): Element | ReactElement {
  const parsedChords = Chords.parseChordsToFrames(chords);
  const target = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const setDimensions = useCallback(() => {
    if (target.current) {
      setWidth(target.current?.offsetWidth);
    }
  }, []);

  const handleResize = (): void => {
    setDimensions();
  };

  useEffect(() => {
    setDimensions();
  }, [setDimensions]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  });

  if (!parsedChords) return <Spinner strokeBaseColor="black" strokeColor="transparent" />;

  return (
    <>
      <ChordDiagramsContainer ref={target}>
        <ChordsContainer>
          {parsedChords.map(chord => {
            const isUkulele = chord.frame.notes.length <= 4;
            const adjustedHeight = Math.max(
              width && width > 560 ? width / 4 : (width || 0) / 2,
              200,
            );
            const adjustedWidth = Math.max(
              width && width > 560 ? width / 5 : (width || 0) / 2.5,
              180,
            );
            const widthChord = Math.min(adjustedWidth, 200);
            const heightChord = isUkulele
              ? Math.min(adjustedHeight, 188)
              : Math.min(adjustedHeight, 230);
            const fingeringSize = Math.floor(widthChord / 14);
            const labelSize = width && width > 560 ? 24 : 18;
            return (
              <svg
                viewBox={`16 0 ${widthChord} ${heightChord}`}
                width={widthChord}
                height={heightChord}
              >
                <ChordDiagram
                  key={chord.frame.label}
                  chordFrame={chord.frame}
                  height={heightChord}
                  width={widthChord}
                  labelFontSize={labelSize}
                  fingeringFontSize={fingeringSize}
                  stringColor="#808080"
                  fretColor="#808080"
                  OpenStringColor="#3d8dc9"
                  dotColor="#3d8dc9"
                />
              </svg>
            );
          })}
        </ChordsContainer>
      </ChordDiagramsContainer>
    </>
  );
}

const ChordOverlay: FC<ChordOverlayProps> = ({ chords = [], title = null }) => {
  return (
    <>
      {title ? <SectionTitle title={title} /> : null}
      {Array.isArray(chords) && chords.length ? renderChordDiagrams(chords) : null}
    </>
  );
};

export default ChordOverlay;
